import React, { useEffect, useMemo, useState } from "react";
import { RootState } from "../../app/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris/property-filter/interfaces";
import {
  PropertyFilterQuery,
  PropertyFilterToken,
} from "@amzn/awsui-collection-hooks/cjs/interfaces";
import {
  FormField,
  PropertyFilter as PropertyFilterComponent,
  Toggle,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { StringField } from "../searchComponents";
import {
  PROPERTY_FILTER_I18N,
  propertyFiltersConfig,
} from "./SearchPropertyFilter-config";
import {
  PropertyFilter,
  PropertyFilterGroup,
  setActivePropertyFilter,
  setQueryPropertyFilters,
  resetActivePropertyFilters,
  setActivePropertyFiltersVisibilityEnabled,
  setQueryPropertyFiltersForceRehydrate,
} from "../searchSideBarConnector";
import "./SearchPropertyFilter.css";

interface PolarisPropertyFilterGroup extends PropertyFilterQuery {
  tokenGroups?: PolarisPropertyFilterGroup[];
}

const SearchPropertyFilter = () => {
  const dispatch = useDispatch();

  const { productLinesData } = useSelector(
    (state: RootState) => state.productLines
  );
  const { programNameData } = useSelector(
    (state: RootState) => state.upstreamAttributesData
  );
  const [asinFilter, setAsinFilter] = useState("");
  const [polarisPropertyFilter, setPolarisPropertyFilter] =
    useState<PolarisPropertyFilterGroup>({
      tokens: [],
      tokenGroups: [],
      operation: "and",
    });
  const polarisPropertyFilterConfig = useMemo(
    () => propertyFiltersConfig(productLinesData, programNameData),
    [productLinesData, programNameData]
  );
  const {
    queryPropertyFilters,
    queryPropertyFiltersForceRehydrate,
    activePropertyFiltersVisibilityEnabled,
  } = useSelector((state: RootState) => state.generateQuery);

  useEffect(() => {
    rehydratePolarisPropertyFilter(queryPropertyFilters);
  }, []);

  useEffect(() => {
    if (queryPropertyFiltersForceRehydrate) {
      rehydratePolarisPropertyFilter(queryPropertyFilters);
      dispatch(setQueryPropertyFiltersForceRehydrate(false));
    }
  }, [queryPropertyFiltersForceRehydrate]);

  // Rehydrate PolarisPropertyFilter state from Search API PropertyFilterGroup
  const rehydratePolarisPropertyFilter = (
    propertyFilterGroup: PropertyFilterGroup
  ): void => {
    if (!propertyFilterGroup || propertyFilterGroup.filters.length === 0) {
      setPolarisPropertyFilter({ tokens: [], operation: "and" });
      setAsinFilter("");
    } else {
      if (propertyFilterGroup.filters?.length > 2) {
        return; // Unrepresentable query
      }
      propertyFilterGroup?.filters.forEach((property) => {
        if (
          property.type === "PropertyFilter" &&
          property.propertyKey === "asin"
        ) {
          setAsinFilter(property.value);
        }
        if (property.type === "PropertyFilterGroup") {
          const polarisPropertyFilter =
            transformPropertyFilterGroupToPolarisPropertyFilter(property, true);
          setPolarisPropertyFilter(
            preProcessPolarisPropertyFilter({ ...polarisPropertyFilter })
          );
        }
      });
    }
  };

  // Dispatch ASIN filter query updates
  // QueryPropertyFilter root state expects a PropertyFilterGroup root value with two children: an ASIN PropertyFilter and
  // a PropertyFilterGroup with the rest of the attribute filters
  const updateQueryFromAsinFilter = (asin: string): void => {
    const filters = queryPropertyFilters?.filters?.filter(
      (f: PropertyFilter | PropertyFilterGroup) =>
        f.type === "PropertyFilterGroup"
    );
    filters.push({
      type: "PropertyFilter",
      operator: "filter",
      value: asin,
      propertyKey: "asin",
    });
    dispatch(
      setQueryPropertyFilters({
        type: "PropertyFilterGroup",
        filters,
        operation: "and",
      })
    );
  };

  // Dispatch Property filter query updates
  // QueryPropertyFilter root state expects a PropertyFilterGroup root value with two children: an ASIN PropertyFilter and
  // a PropertyFilterGroup with the rest of the attribute filters
  const updateQueryFromPropertyFilters = (
    query: PropertyFilterProps.Query
  ): void => {
    const filters = queryPropertyFilters?.filters?.filter(
      (f: PropertyFilter | PropertyFilterGroup) =>
        f.type === "PropertyFilter" && f.propertyKey === "asin"
    );
    let attributeFilters =
      transformPolarisPropertyFilterToPropertyFilterGroup(query);
    if (attributeFilters?.filters.length > 0) {
      filters.push(attributeFilters);
    }
    dispatch(
      setQueryPropertyFilters({
        type: "PropertyFilterGroup",
        filters,
        operation: "and",
      })
    );
  };

  // Remove not allowed filters, filter values and keep track of current active filters
  const preProcessPolarisPropertyFilter = (
    query: any,
    root = true
  ): PolarisPropertyFilterGroup => {
    if (root) {
      dispatch(resetActivePropertyFilters());
    }
    const subQuery: any = {
      tokens: [],
      tokenGroups: [],
      operation: query.operation ?? "and",
    };
    if (query?.operation) {
      const tokens = root ? query?.tokenGroups : query?.tokens;
      (tokens ?? []).forEach((token: any) => {
        if (token?.operation) {
          let nestedTokens = preProcessPolarisPropertyFilter(token, false);
          if (nestedTokens?.tokens.length > 0) {
            if (root) {
              subQuery.tokenGroups.push(nestedTokens);
            } else {
              subQuery.tokens.push(nestedTokens);
            }
          }
        } else if (
          token?.propertyKey &&
          polarisPropertyFilterConfig[token?.propertyKey]
        ) {
          if (token?.value && token?.value?.toString()?.trim() !== "") {
            if (root) {
              subQuery.tokenGroups.push(token);
            } else {
              subQuery.tokens.push(token);
            }
            dispatch(setActivePropertyFilter(token?.propertyKey));
          }
        }
      });
    }
    return subQuery;
  };

  // Transform Polaris PropertyFilter to Search API PropertyFilterGroup
  const transformPolarisPropertyFilterToPropertyFilterGroup = (
    group: PolarisPropertyFilterGroup
  ): PropertyFilterGroup => {
    const propertyFilterGroup: PropertyFilterGroup = {
      type: "PropertyFilterGroup",
      filters: [],
      operation: group.operation,
    };
    [...(group?.tokens ?? []), ...(group?.tokenGroups ?? [])].forEach(
      (token: PolarisPropertyFilterGroup | PropertyFilterToken) => {
        if ("operation" in token) {
          propertyFilterGroup.filters.push(
            transformPolarisPropertyFilterToPropertyFilterGroup(token)
          );
        } else if ("propertyKey" in token) {
          propertyFilterGroup.filters.push(
            transformPolarisPropertyFilterTokenToPropertyFilter(token)
          );
        }
      }
    );
    return propertyFilterGroup;
  };

  // Transform Search API PropertyFilterGroup to Polaris PropertyFilterGroup
  const transformPropertyFilterGroupToPolarisPropertyFilter = (
    propertyFilterGroup: PropertyFilterGroup,
    root = false
  ): PolarisPropertyFilterGroup => {
    const group: any = {
      tokens: [],
      tokenGroups: [],
      operation: propertyFilterGroup.operation,
    };
    (propertyFilterGroup?.filters ?? []).forEach(
      (property: PropertyFilter | PropertyFilterGroup) => {
        if (property.type === "PropertyFilterGroup") {
          if (root) {
            group.tokenGroups.push(
              transformPropertyFilterGroupToPolarisPropertyFilter(
                property,
                false
              )
            );
          } else {
            group.tokens.push(
              transformPropertyFilterGroupToPolarisPropertyFilter(
                property,
                false
              )
            );
          }
        } else if (property.type === "PropertyFilter") {
          if (root) {
            group.tokenGroups.push(
              transformPropertyFilterToPolarisPropertyFilterToken(property)
            );
          } else {
            group.tokens.push(
              transformPropertyFilterToPolarisPropertyFilterToken(property)
            );
          }
        }
      }
    );
    return group;
  };

  // Transform Polaris PropertyFilterToken to Search API PropertyFilter
  const transformPolarisPropertyFilterTokenToPropertyFilter = (
    propertyFilterToken: PropertyFilterToken
  ): PropertyFilter => {
    return {
      type: "PropertyFilter",
      propertyKey: propertyFilterToken?.propertyKey ?? "",
      value: propertyFilterToken?.value ?? "",
      operator: propertyFilterToken?.operator === ":" ? "fuzzy" : "filter",
    };
  };

  // Transform Search API PropertyFilter to Polaris PropertyFilterToken
  const transformPropertyFilterToPolarisPropertyFilterToken = (
    propertyFilter: PropertyFilter
  ): PropertyFilterToken => {
    return {
      propertyKey: propertyFilter?.propertyKey,
      value: propertyFilter?.value ?? "",
      operator: propertyFilter?.operator === "fuzzy" ? ":" : "=",
    };
  };

  return (
    <SpaceBetween size="s" className={"search-property-filter-form"}>
      <FormField id={"asin-form"} label="ASIN" stretch={true}>
        {StringField(
          "B004AT6YQN, B00CC8XHN9, B00CUTCNXM, B00KBNMT06 ...",
          "asin",
          asinFilter as string,
          (event: any) => {
            setAsinFilter(event?.payload?.queryState?.value);
            updateQueryFromAsinFilter(event?.payload?.queryState?.value);
          }
        )}
      </FormField>
      <FormField
        id={"property-filter-form"}
        label="More attributes"
        stretch={true}
      >
        <PropertyFilterComponent
          query={polarisPropertyFilter}
          onChange={({ detail }) => {
            const propertyFilter = preProcessPolarisPropertyFilter({
              ...detail,
            });
            setPolarisPropertyFilter(propertyFilter);
            updateQueryFromPropertyFilters(propertyFilter);
          }}
          expandToViewport
          i18nStrings={PROPERTY_FILTER_I18N}
          enableTokenGroups
          customGroupsText={[]}
          freeTextFiltering={{ operators: ["="], defaultOperator: "=" }}
          disableFreeTextFiltering={true}
          virtualScroll={true}
          filteringAriaLabel="Filter based on attribute values"
          filteringPlaceholder="Filter based on attribute values"
          filteringOptions={
            Object.entries(polarisPropertyFilterConfig)?.flatMap(
              ([_, property]) => property?.filteringOptions ?? []
            ) ?? []
          }
          filteringProperties={
            Object.entries(polarisPropertyFilterConfig)?.map(
              ([_, property]) => property.filteringProperty
            ) ?? []
          }
        />
      </FormField>
      <FormField
        id={"property-filter-visibility"}
        label="Auto-enable attribute visibility"
        stretch={true}
      >
        <Toggle
          checked={activePropertyFiltersVisibilityEnabled}
          onChange={({ detail }) => {
            dispatch(setActivePropertyFiltersVisibilityEnabled(detail.checked));
          }}
        />
      </FormField>
    </SpaceBetween>
  );
};

export default SearchPropertyFilter;
